/**
 * Phase V1.1-WP5 — useSessionTicker (the ONLY net-new component in V1.1).
 *
 * Client-side hook that derives a live `mm:ss` (or `Hh Mm`) elapsed
 * duration from the server-issued `session_started_at`. Server is the
 * authoritative source on commit; this hook is presentational only.
 *
 * Contract:
 *   const { elapsedMs, label } = useSessionTicker({
 *     startedAt:      reservation.session_started_at,    // ISO string
 *     pausedAt:       reservation.session_paused_at,     // ISO string | null
 *     pausedTotalMs:  reservation.session_paused_total_ms ?? 0,
 *     sessionState:   reservation.session_state,         // 'active'|'paused'|'ended'|null
 *   });
 *
 * Semantics:
 *   • `elapsedMs` = (now − startedAt) − pausedTotalMs
 *     If `sessionState === 'paused'`, the clock freezes at the moment
 *     `pausedAt` was set, so subsequent ticks do NOT accrue.
 *   • Returns `{elapsedMs: 0, label: '—'}` when no start timestamp.
 *   • Tick interval: 1 second. No animation, no jitter — operator-locked
 *     "static" per OQ-V1.1-5.
 *
 * Reuse-first: ZERO new dependencies — uses React's built-in useEffect
 * + useState. ~30 LOC per package §11 sizing.
 */
import { useEffect, useState } from 'react';

function _formatElapsed(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (hours > 0) {
    return `${hours}h ${String(mins).padStart(2, '0')}m`;
  }
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export default function useSessionTicker({
  startedAt,
  pausedAt = null,
  pausedTotalMs = 0,
  sessionState = null,
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // Don't tick when there's no session or it's ended/paused — saves
    // unnecessary re-renders. Resumes on session_state transitions
    // because the parent re-mounts with new props.
    if (!startedAt || sessionState === 'ended' || sessionState === 'paused') {
      return undefined;
    }
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [startedAt, sessionState]);

  if (!startedAt) return { elapsedMs: 0, label: '—' };
  const start = new Date(startedAt).getTime();
  if (!Number.isFinite(start)) return { elapsedMs: 0, label: '—' };
  // Freeze elapsed at pause moment when paused.
  const referenceNow =
    sessionState === 'paused' && pausedAt
      ? new Date(pausedAt).getTime()
      : now;
  const elapsedMs = Math.max(0, referenceNow - start - (pausedTotalMs || 0));
  return { elapsedMs, label: _formatElapsed(elapsedMs) };
}

export { _formatElapsed };
